// src/pages/PlayerPage.js - WITH ALL BACKGROUND MANAGEMENT REMOVED
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import '../styles/PlayerPage.css';
import MessageSender from '../components/MessageSender';

// YouTube Configuration - will be loaded from config file
let YOUTUBE_CHANNEL_ID = '';
let YOUTUBE_API_KEY = '';

const PlayerPage = () => {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  
  // Add state for config loading
  const [configLoaded, setConfigLoaded] = useState(false);
  
  // YouTube Live Stream State
  const [youtubeLiveState, setYoutubeLiveState] = useState({
    isLive: false,
    videoId: null,
    isFullscreen: false,
    isMinimized: false
  });
  const youtubePlayerRef = useRef(null);
  const youtubeIntervalRef = useRef(null);
  
  // Audio state tracking refs
  const wasRadioPlayingRef = useRef(false);
  
  // Get everything from context
  const {
    stations,
    currentStation: globalStation,
    isPlaying: globalIsPlaying,
    isCatchupMode,
    setCurrentStation,
    togglePlayback,
    switchToLiveRadio,
    metadata,
    recentTracks,
    fetchMetadataForStation,
    getLiveTrackInfo,
    getLiveShowArt,
    getLiveShowTitle,
  } = useAudioPlayer();

  // Get metadata using the same pattern as GlobalPlayerBar
  const trackInfoData = getLiveTrackInfo();
  const showArt =
    trackInfoData?.artwork_urls?.large ||
    trackInfoData?.artwork_urls?.standard ||
    getLiveShowArt();                       // ← CHANGED fallback
  const showTitle = trackInfoData?.title || getLiveShowTitle(); // ← CHANGED
  const trackArtist = trackInfoData?.track_artist || '';
  const trackTitle = trackInfoData?.track_title || '';
  const trackAlbum = trackInfoData?.track_album || '';
  
  // Format track info string like GlobalPlayerBar
  const formattedTrackInfo = trackArtist ? 
    `${trackArtist} - ${trackTitle}${trackAlbum && trackAlbum !== 'Unknown' ? ` (${trackAlbum})` : ''}` : 
    trackTitle || 'No track information';

  // Simple display station from URL
  const [displayStation, setDisplayStation] = useState(() => {
    if (stationId) {
      const station = stations.find(s => s.id === stationId);
      return station || stations[0];
    }
    return stations[0];
  });
  
  const [currentIndex, setCurrentIndex] = useState(() => {
    const station = displayStation;
    if (!station || !station.id) return 0;
    const index = stations.findIndex(s => s.id === station.id);
    return index >= 0 ? index : 0;
  });
  
  const [visibleStations, setVisibleStations] = useState([]);
  const [notification, setNotification] = useState(null);
  
  // Track metadata for display
  const [stationMetadata, setStationMetadata] = useState({
    showTitle: '',
    trackInfo: '',
    albumArt: '',
    isOnline: true
  });

  // Cache for Apple Music artwork URLs and track URLs
  const artworkCache = useRef(new Map());
  
  // Track which recent tracks need artwork fetching
  const [appleMusicArtwork, setAppleMusicArtwork] = useState({});
  const [appleMusicUrls, setAppleMusicUrls] = useState({});

  // REMOVED ALL: canvasRef, backgroundVideoRef, animationFrameRef
  // REMOVED: All useEffect hooks for background management
  // REMOVED: Canvas element from JSX
  // No background manipulation at all - app default will show

  // Load YouTube configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch('/youtube-config.json');
        const config = await response.json();
        YOUTUBE_CHANNEL_ID = config.channelId;
        YOUTUBE_API_KEY = config.apiKey;
        setConfigLoaded(true);
      } catch (error) {
        console.error('Error loading YouTube configuration:', error);
        // Set config loaded even on error to not block the app
        setConfigLoaded(true);
      }
    };
    
    loadConfig();
  }, []);

  // Function to check if artwork is the unwanted default image
  const isUnwantedArtwork = (url) => {
    if (!url) return false;
    if (url.includes('asurahosting.com')) {
      return true;
    }
    if (url.includes('placeholder.com') || url.includes('via.placeholder')) {
      return true;
    }
    return false;
  };

  // Apple Music API integration
  const searchAppleMusic = useCallback(async (artist, title) => {
    if (!artist || !title) return { artwork: null, trackUrl: null };
    
    const cacheKey = `${artist}-${title}`.toLowerCase();
    
    if (artworkCache.current.has(cacheKey)) {
      const cached = artworkCache.current.get(cacheKey);
      return {
        artwork: cached.artwork === 'notfound' ? null : cached.artwork,
        trackUrl: cached.trackUrl === 'notfound' ? null : cached.trackUrl
      };
    }
    
    try {
      const searchTerm = `${encodeURIComponent(artist)}%20${encodeURIComponent(title)}`;
      const url = `https://itunes.apple.com/search?term=${searchTerm}&entity=song&limit=1`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        let artworkUrl = result.artworkUrl100;
        if (artworkUrl) {
          artworkUrl = artworkUrl.replace('100x100bb', '600x600bb');
        }
        const trackUrl = result.trackViewUrl || null;
        
        artworkCache.current.set(cacheKey, {
          artwork: artworkUrl || 'notfound',
          trackUrl: trackUrl || 'notfound'
        });
        
        return { artwork: artworkUrl, trackUrl };
      }
      
      artworkCache.current.set(cacheKey, {
        artwork: 'notfound',
        trackUrl: 'notfound'
      });
      
      return { artwork: null, trackUrl: null };
    } catch (error) {
      console.error('Error searching Apple Music API:', error);
      return { artwork: null, trackUrl: null };
    }
  }, []);

  // Fetch artwork for recent tracks
  const fetchMissingArtwork = useCallback(async (tracks) => {
    if (!tracks || tracks.length === 0) return;
    
    const artworkUpdates = {};
    const urlUpdates = {};
    
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      
      if (!track.song?.art || isUnwantedArtwork(track.song.art)) {
        const cacheKey = `${track.song?.artist}-${track.song?.title}`.toLowerCase();
        
        if (artworkCache.current.has(cacheKey)) {
          const cached = artworkCache.current.get(cacheKey);
          if (cached.artwork !== 'notfound') {
            artworkUpdates[cacheKey] = cached.artwork;
          }
          if (cached.trackUrl !== 'notfound') {
            urlUpdates[cacheKey] = cached.trackUrl;
          }
          continue;
        }
        
        const result = await searchAppleMusic(
          track.song?.artist,
          track.song?.title
        );
        
        if (result.artwork) {
          artworkUpdates[cacheKey] = result.artwork;
        }
        
        if (result.trackUrl) {
          urlUpdates[cacheKey] = result.trackUrl;
        }
        
        if (i < tracks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }
    
    if (Object.keys(artworkUpdates).length > 0) {
      setAppleMusicArtwork(prev => ({
        ...prev,
        ...artworkUpdates
      }));
    }
    
    if (Object.keys(urlUpdates).length > 0) {
      setAppleMusicUrls(prev => ({
        ...prev,
        ...urlUpdates
      }));
    }
  }, [searchAppleMusic]);

  // YouTube Live Stream Functions
  const checkYouTubeLiveStatus = useCallback(async () => {
    if (!YOUTUBE_CHANNEL_ID || !YOUTUBE_API_KEY) {
      console.warn('YouTube configuration not loaded');
      return;
    }
    
    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${YOUTUBE_CHANNEL_ID}&eventType=live&type=video&key=${YOUTUBE_API_KEY}`
      );
      const data = await response.json();

      if (data.items && data.items.length > 0) {
        const liveVideo = data.items[0];
        setYoutubeLiveState(prev => ({
          ...prev,
          isLive: true,
          videoId: liveVideo.id.videoId,
          isMinimized: prev.isMinimized || false
        }));
      } else {
        setYoutubeLiveState(prev => {
          if (prev.isLive) {
            return {
              isLive: false,
              videoId: null,
              isFullscreen: false,
              isMinimized: false
            };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error checking YouTube live status:', error);
    }
  }, []);

  const openFullscreen = useCallback(() => {
    // Remember if radio was playing
    wasRadioPlayingRef.current = globalIsPlaying;
    
    // Pause the radio
    if (globalIsPlaying) {
      togglePlayback();
    }
    
    setYoutubeLiveState(prev => ({
      ...prev,
      isFullscreen: true,
      isMinimized: false
    }));
  }, [globalIsPlaying, togglePlayback]);

  const minimizeFromFullscreen = useCallback(() => {
    if (youtubePlayerRef.current) {
      youtubePlayerRef.current.destroy();
      youtubePlayerRef.current = null;
    }
    
    setYoutubeLiveState(prev => ({
      ...prev,
      isFullscreen: false,
      isMinimized: true
    }));

    // Resume radio if it was playing before
    setTimeout(() => {
      if (wasRadioPlayingRef.current) {
        // Switch to live to get current stream
        switchToLiveRadio(displayStation);
        wasRadioPlayingRef.current = false;
      }
    }, 300);
  }, [displayStation, switchToLiveRadio]);

  const closePreview = useCallback(() => {
    if (youtubePlayerRef.current) {
      youtubePlayerRef.current.destroy();
      youtubePlayerRef.current = null;
    }
    
    setYoutubeLiveState(prev => ({
      ...prev,
      isMinimized: false
    }));

    // Resume radio if it was playing before
    if (youtubeLiveState.isFullscreen && wasRadioPlayingRef.current) {
      setTimeout(() => {
        switchToLiveRadio(displayStation);
        wasRadioPlayingRef.current = false;
      }, 300);
    }
  }, [youtubeLiveState.isFullscreen, displayStation, switchToLiveRadio]);

  // Auto-close fullscreen when stream ends
  useEffect(() => {
    if (!youtubeLiveState.isLive && youtubeLiveState.isFullscreen) {
      minimizeFromFullscreen();
    }
  }, [youtubeLiveState.isLive, youtubeLiveState.isFullscreen, minimizeFromFullscreen]);

  // Update recent tracks when they change
  useEffect(() => {
    const currentTracks = getCurrentRecentTracks();
    if (currentTracks.length > 0) {
      fetchMissingArtwork(currentTracks);
    }
  }, [recentTracks, displayStation?.id, fetchMissingArtwork]);

  // Start polling for YouTube live status (only when config is loaded)
  useEffect(() => {
    if (!configLoaded) return;
    
    checkYouTubeLiveStatus();
    youtubeIntervalRef.current = setInterval(checkYouTubeLiveStatus, 30000);

    return () => {
      if (youtubeIntervalRef.current) {
        clearInterval(youtubeIntervalRef.current);
      }
    };
  }, [checkYouTubeLiveStatus, configLoaded]);

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        console.log('YouTube API ready');
      };
    }
  }, []);

  // Initialize YouTube player when entering fullscreen
  useEffect(() => {
    if (youtubeLiveState.isFullscreen && youtubeLiveState.videoId) {
      const initPlayer = () => {
        if (window.YT && window.YT.Player) {
          youtubePlayerRef.current = new window.YT.Player('youtube-fullscreen-player', {
            videoId: youtubeLiveState.videoId,
            playerVars: {
              autoplay: 1,
              controls: 1,
              modestbranding: 1,
              rel: 0,
              showinfo: 0
            },
            events: {
              onReady: (event) => {
                event.target.playVideo();
              },
              onStateChange: (event) => {
                if (event.data === window.YT.PlayerState.ENDED) {
                  minimizeFromFullscreen();
                }
              }
            }
          });
        } else {
          setTimeout(initPlayer, 100);
        }
      };

      initPlayer();
    }

    return () => {
      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.destroy();
        youtubePlayerRef.current = null;
      }
    };
  }, [youtubeLiveState.isFullscreen, youtubeLiveState.videoId, minimizeFromFullscreen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (youtubeIntervalRef.current) {
        clearInterval(youtubeIntervalRef.current);
      }
      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.destroy();
      }
      // Resume radio if needed
      if (wasRadioPlayingRef.current) {
        switchToLiveRadio(displayStation);
      }
    };
  }, [displayStation, switchToLiveRadio]);

  // If user hits /player with no stationId, go to last selected station
  useEffect(() => {
    if (stationId) return;
    if (!stations?.length) return;

    const id =
      (globalStation?.id && stations.some(s => s.id === globalStation.id))
        ? globalStation.id
        : stations[0].id;

    navigate(`/player/${id}`, { replace: true });
  }, [stationId, stations, globalStation, navigate]);

  // Update display station when URL changes
  useEffect(() => {
    if (stationId && stations.length > 0) {
      const index = stations.findIndex(s => s.id === stationId);
      if (index !== -1) {
        const station = stations[index];
        setCurrentIndex(index);
        setDisplayStation(station);
        
        setStationMetadata({
          showTitle: station.name,
          trackInfo: `Genre: ${station.genre || 'Unknown'}`,
          albumArt: station.showArt || '',
          isOnline: true
        });
        
        if (!isCatchupMode && globalStation?.id !== station.id) {
          setCurrentStation(station, false);
        }
        
        fetchStationMetadata(station);
      }
    }
  }, [stationId, stations, setCurrentStation, isCatchupMode]);

  // Fetch metadata for a station
  const fetchStationMetadata = async (station) => {
    if (!station || !station.id) return;
    
    try {
      const data = await fetchMetadataForStation(station.id);
      
      if (data) {
        const song = data.now_playing?.song;
        const show = data.live?.streamer_name || 'JukeBox';
        const info = song ? `${song.artist} — ${song.title}` : `Genre: ${station.genre || 'Unknown'}`;
        
        let albumArt = '';
        if (song?.art && !isUnwantedArtwork(song.art)) {
          albumArt = song.art;
        } else if (song) {
          const result = await searchAppleMusic(song.artist, song.title);
          albumArt = result.artwork || song.art || station.showArt || '';
        } else {
          albumArt = station.showArt || '';
        }
        
        setStationMetadata({
          showTitle: show,
          trackInfo: info,
          albumArt: albumArt,
          isOnline: data.is_online !== false
        });
      } else {
        setStationMetadata({
          showTitle: station.name,
          trackInfo: `Genre: ${station.genre || 'Unknown'}`,
          albumArt: station.showArt || '',
          isOnline: true
        });
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
      setStationMetadata({
        showTitle: station.name,
        trackInfo: `Genre: ${station.genre || 'Unknown'}`,
        albumArt: station.showArt || '',
        isOnline: true
      });
    }
  };

  // Update metadata based on context's getTrackInfo
  useEffect(() => {
    if (displayStation && trackInfoData) {
      const currentShowTitle = showTitle || displayStation.name || 'Select a station';
      const currentTrackInfo = formattedTrackInfo || `Genre: ${displayStation.genre || 'Unknown'}`;
      
      let currentAlbumArt = showArt || displayStation.showArt || '';
      
      if (currentAlbumArt && isUnwantedArtwork(currentAlbumArt)) {
        if (trackArtist && trackTitle) {
          searchAppleMusic(trackArtist, trackTitle).then(result => {
            if (result.artwork && !isUnwantedArtwork(result.artwork)) {
              setStationMetadata(prev => ({
                ...prev,
                albumArt: result.artwork
              }));
            }
          });
        }
      }
      
      setStationMetadata(prev => ({
        ...prev,
        showTitle: currentShowTitle,
        trackInfo: currentTrackInfo,
        albumArt: currentAlbumArt || prev.albumArt,
        isOnline: true
      }));
    }
  }, [trackInfoData, showTitle, showArt, formattedTrackInfo, displayStation, searchAppleMusic, trackArtist, trackTitle]);

  // Get recent tracks for current station
  const getCurrentRecentTracks = () => {
    if (!recentTracks || !displayStation?.id) return [];
    return recentTracks[displayStation.id] || [];
  };

  // REMOVED: Set CSS variables and body background useEffect
  // No longer setting any background properties

  // Update visible stations for carousel
  useEffect(() => {
    if (stations.length === 0) return;
    
    const getVisibleStations = () => {
      const total = stations.length;
      const stationsList = [];
      
      for (let i = -2; i <= 2; i++) {
        let index = currentIndex + i;
        
        if (index < 0) {
          index = total + index;
        } else if (index >= total) {
          index = index % total;
        }
        
        stationsList.push({
          ...stations[index],
          originalIndex: index,
          isActive: i === 0
        });
      }
      
      return stationsList;
    };
    
    setVisibleStations(getVisibleStations());
  }, [currentIndex, stations]);

  // Format time ago for recent tracks
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const now = new Date();
    let playedAt;
    
    if (typeof timestamp === 'number') {
      if (timestamp < 10000000000) {
        playedAt = new Date(timestamp * 1000);
      } else {
        playedAt = new Date(timestamp);
      }
    } else if (typeof timestamp === 'string') {
      playedAt = new Date(timestamp);
    } else {
      return 'Recently';
    }
    
    if (isNaN(playedAt.getTime())) return 'Recently';
    
    const diffMs = now - playedAt;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} days ago`;
  };

  // Station selection
  const handleStationSelect = (index) => {
    const station = stations[index];
    navigate(`/player/${station.id}`);
  };

  const handlePrevClick = () => {
    const newIndex = (currentIndex - 1 + stations.length) % stations.length;
    handleStationSelect(newIndex);
  };

  const handleNextClick = () => {
    const newIndex = (currentIndex + 1) % stations.length;
    handleStationSelect(newIndex);
  };

  // Handle play/pause
  const handlePlayPause = () => {
    if (isCatchupMode) {
      switchToLiveRadio(displayStation);
    } else {
      if (globalStation?.id === displayStation?.id && globalIsPlaying) {
        togglePlayback();
      } else {
        setCurrentStation(displayStation, true);
      }
    }
  };

  // Get Apple Music URL for a track
  const getAppleMusicUrl = (track) => {
    const cacheKey = `${track.song?.artist}-${track.song?.title}`.toLowerCase();
    return appleMusicUrls[cacheKey] || null;
  };

  // Open Apple Music in new tab
  const openInAppleMusic = (track, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const appleMusicUrl = getAppleMusicUrl(track);
    
    if (appleMusicUrl) {
      window.open(appleMusicUrl, '_blank', 'noopener,noreferrer');
    } else {
      showNotification('Searching for track on Apple Music...');
      searchAppleMusic(track.song?.artist, track.song?.title).then(result => {
        if (result.trackUrl) {
          const cacheKey = `${track.song?.artist}-${track.song?.title}`.toLowerCase();
          setAppleMusicUrls(prev => ({
            ...prev,
            [cacheKey]: result.trackUrl
          }));
          window.open(result.trackUrl, '_blank', 'noopener,noreferrer');
        } else {
          showNotification('Track not found on Apple Music');
        }
      });
    }
  };

  // Handle image load error
  const handleImageError = (e, track) => {
    const cacheKey = `${track.song?.artist}-${track.song?.title}`.toLowerCase();
    
    if (appleMusicArtwork[cacheKey]) {
      e.target.style.backgroundImage = `url('${appleMusicArtwork[cacheKey]}')`;
      return;
    }
    
    searchAppleMusic(track.song?.artist, track.song?.title)
      .then(result => {
        if (result.artwork) {
          setAppleMusicArtwork(prev => ({
            ...prev,
            [cacheKey]: result.artwork
          }));
          e.target.style.backgroundImage = `url('${result.artwork}')`;
        } else {
          if (track.song?.art) {
            e.target.style.backgroundImage = `url('${track.song.art}')`;
          } else {
            e.target.style.backgroundImage = 'none';
            e.target.style.backgroundColor = 'var(--station-color, #8c52ff)';
          }
        }
        
        if (result.trackUrl) {
          setAppleMusicUrls(prev => ({
            ...prev,
            [cacheKey]: result.trackUrl
          }));
        }
      });
  };

  // Share button handler
  const handleShare = async () => {
    if (!displayStation) return;

    const currentShowTitle = stationMetadata.showTitle || '';
    const currentTrackInfo = stationMetadata.trackInfo || '';

    const shareData = {
      title: `Listen to ${displayStation.name} on Utter Radio`,
      text: `Currently playing: ${currentShowTitle} - ${currentTrackInfo}`,
      url: new URL(window.location.href).toString(),
    };

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.warn('navigator.share failed, falling back:', err);
      }
    }

    fallbackShare();
  };

  const fallbackShare = async () => {
    const currentShowTitle = stationMetadata.showTitle || '';
    const currentTrackInfo = stationMetadata.trackInfo || '';
    const textToCopy = `${displayStation.name} - ${currentShowTitle}\n${currentTrackInfo}\n${new URL(window.location.href).toString()}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        showNotification('Link copied to clipboard!');
        return;
      }

      const ta = document.createElement('textarea');
      ta.value = textToCopy;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);

      ta.select();
      ta.setSelectionRange(0, ta.value.length);

      const ok = document.execCommand('copy');
      document.body.removeChild(ta);

      if (ok) {
        showNotification('Link copied to clipboard!');
      } else {
        showNotification(`Share this link: ${window.location.href}`);
      }
    } catch (e) {
      showNotification(`Share this link: ${window.location.href}`);
    }
  };

  const handleSchedule = () => {
    navigate('/schedule');
  };

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  // Use local station metadata for display
  const { showTitle: displayShowTitle, trackInfo: displayTrackInfo, albumArt, isOnline } = stationMetadata;
  const recentTracksAPI = getCurrentRecentTracks();

  // Get the correct icon for play/pause button
  const getPlayPauseIcon = () => {
    if (isCatchupMode) {
      return 'fa-play';
    }
    if (globalIsPlaying && globalStation?.id === displayStation?.id) {
      return 'fa-stop';
    }
    return 'fa-play';
  };

  // Get the correct playing state for album art animation
  const isAlbumArtPlaying = () => {
    return globalIsPlaying && !isCatchupMode && globalStation?.id === displayStation?.id;
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    newsletter: true
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormStatus({ submitting: true, submitted: false, error: false });

    try {
      const response = await fetch(
        'https://formsubmit.co/ajax/utterradiouk@gmail.com',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
            _subject: `New Contact Form Submission: ${formData.subject || 'No Subject'}`,
            _template: 'table',
            _captcha: 'false'
          })
        }
      );

      if (!response.ok) throw new Error('Submit failed');

      setFormStatus({ submitting: false, submitted: true, error: false });

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        newsletter: true
      });

      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, submitted: false }));
      }, 5000);

    } catch (err) {
      console.error(err);
      setFormStatus({ submitting: false, submitted: false, error: true });
    }
  };

  return (
    <>
      <div className="pageContainer">
      
      {/* REMOVED: Canvas element */}
      
      {/* Main Content */}
      <div className="main-container">
        {/* Show Info Section */}
        <div className="show-info-section">
          <div className="now-playing-content">
            <div className="album-art-container">
              <div 
                className={`album-art-large ${isAlbumArtPlaying() ? 'playing' : ''}`}
                style={{ 
                  backgroundImage: albumArt && !isUnwantedArtwork(albumArt) ? `url('${albumArt}')` : 'none',
                  backgroundColor: albumArt && !isUnwantedArtwork(albumArt) ? 'transparent' : 'var(--primary)'
                }}
              >
                <button 
                  className="play-pause-btn-overlay" 
                  onClick={handlePlayPause}
                >
                  <i className={`fas ${getPlayPauseIcon()}`}></i>
                </button>
              </div>
            </div>
            
            <div className="now-playing-info-right">
              <div className="status-indicator-container">
              </div>
              
              <div className="show-title">
                <span>{trackTitle || 'Failed to load data'}</span>
              </div>
              <div className="track-title">
                {trackArtist || 'Failed to load data'}
              </div>
              
              <div className="action-buttons">
                <button className="action-btn share" onClick={handleShare}
                aria-label="Share this station">
                  <i className="fas fa-share-alt"></i> Share
                </button>
                <button className="action-btn schedule" onClick={handleSchedule}
                aria-label="View schedule">
                  <i className="fa-regular fa-clock"></i> Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Recent Tracks Section */}
        <div className="recent-tracks-section">
          <div className="recent-tracks-header">
            <h2>Contact Station</h2>
          </div>
          
          <div className="recent-tracks-horizontal">
            <div className="list">
              <ul>
                <li>
                  <a href="mailto:utterradiouk@gmail.com">
                    <i className="fas fa-envelope"></i>
                    <span className="desktop-text"> utterradiouk@gmail.com</span>
                    <span className="mobile-text">Email</span>
                  </a>
                </li>
                <li>
                  <a href="https://chat.whatsapp.com/LgFLnsIbKbEDkiyRidxbbL" target="_blank" aria-label="Whatsapp" rel="noreferrer">
                    <i className="fa-brands fa-whatsapp"></i>
                    <span className="desktop-text"> Join Whatsapp Group</span>
                    <span className="mobile-text">WhatsApp</span>
                  </a>
                </li>
              </ul>
            </div>
            <div className="messaging">
              <MessageSender />
            </div>
          </div>
        </div>
      </div>
      
      {notification && (
        <div className="notification-toast">
          <div className="notification-content">
            <i className="fas fa-info-circle"></i>
            <span>{notification}</span>
            <button 
              className="notification-close"
              onClick={() => setNotification(null)}
              aria-label="Close notification"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* YouTube Live Stream Preview */}
      {youtubeLiveState.isLive && !youtubeLiveState.isFullscreen && (
        <div className="youtube-live-preview">
          <div className="youtube-preview-content">
            <button 
              className="youtube-preview-close"
              onClick={closePreview}
              aria-label="Close preview"
            >
              <i className="fas fa-times"></i>
            </button>
            
            <div 
              className="youtube-preview-video"
              onClick={openFullscreen}
            >
              <iframe
                width="300"
                height="169"
                src={`https://www.youtube.com/embed/${youtubeLiveState.videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&showinfo=0`}
                title="YouTube Live Stream Preview"
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen={false}
              />
              
              <div className="youtube-preview-overlay">
                <div className="expand-icon">
                  <i className="fas fa-expand"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* YouTube Fullscreen Overlay */}
      {youtubeLiveState.isFullscreen && (
        <div className="youtube-fullscreen-overlay">
          <div className="youtube-fullscreen-container">
            <button 
              className="youtube-fullscreen-minimize"
              onClick={minimizeFromFullscreen}
              aria-label="Minimize stream"
            >
              <i className="fas fa-compress-alt"></i>
              <span>Minimize</span>
            </button>
            
            <div 
              id="youtube-fullscreen-player"
              className="youtube-fullscreen-player"
            />
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default PlayerPage;